import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ArrowRight, Bell, MessageSquareHeart, Nfc, Palette, QrCode, Send } from 'lucide-react';
export const Route = createFileRoute('/_layout/blog/')({
    component: BlogIndexPage,
    head: () => ({
        meta: [
            { title: 'Blog — Adora Analytics' },
            {
                name: 'description',
                content: 'Articles on offline marketing analytics, QR best practices, Telegram bot setup, real-time notifications, and product announcements from Adora.',
            },
            { property: 'og:title', content: 'Blog — Adora Analytics' },
            {
                property: 'og:description',
                content: 'Articles on offline marketing analytics, QR best practices, Telegram bot setup, real-time notifications, and product announcements from Adora.',
            },
        ],
    }),
});
const POSTS = [
    {
        slug: 'nfc-smart-link',
        title: "Why you shouldn't put a fixed link directly on your NFC tag",
        description: 'Writing a Facebook page or menu URL straight onto an NFC tag locks you in. A smart link keeps the tag, lets the destination change, and makes every tap measurable.',
        date: '2026-05-20',
        tag: 'Guide',
        icon: <Nfc className="h-5 w-5"/>,
    },
    {
        slug: 'page-design-themes',
        title: '35 themes × 42 animated backgrounds: inside Adora’s page-design system',
        description: 'How we built a design system that ships 35 themes and 42 animated backgrounds, why we treat them as two separate axes, and how mode-aware rendering keeps the same theme looking right in showroom, classic, and feedback modes.',
        date: '2026-05-08',
        tag: 'Product',
        icon: <Palette className="h-5 w-5"/>,
    },
    {
        slug: 'feedback-campaigns',
        title: 'Introducing Feedback Campaigns',
        description: 'Turn any QR or smart link into a public feedback form. Submissions land in Telegram in real time and stay attributed to the scan that produced them.',
        date: '2026-04-28',
        tag: 'Announcement',
        icon: <MessageSquareHeart className="h-5 w-5"/>,
    },
    {
        slug: 'qr-best-practices',
        title: 'QR best practices for restaurants and retail',
        description: 'Where to place codes, what to put on the call-to-action, why scan counts ≠ insight, and the small print that makes the difference between 5% and 25% conversion.',
        date: '2026-04-28',
        tag: 'Guide',
        icon: <QrCode className="h-5 w-5"/>,
    },
    {
        slug: 'telegram-bot-setup',
        title: 'Set up the Adora Telegram bot in 60 seconds',
        description: 'Add the bot to your chat, send /chatid, paste it back into Adora. That’s it — your campaigns can now route notifications and feedback to a Telegram chat your team already lives in.',
        date: '2026-04-28',
        tag: 'How-to',
        icon: <Send className="h-5 w-5"/>,
    },
    {
        slug: 'telegram-notifications',
        title: 'Real-time Telegram notifications: what Adora sends, where, and why',
        description: 'Feedback submissions, payout reminders, photo nudges. The full map of Adora’s outbound notifications, how to configure them per workspace and per campaign, and how to test before you ship.',
        date: '2026-04-28',
        tag: 'Reference',
        icon: <Bell className="h-5 w-5"/>,
    },
];
function BlogIndexPage() {
    return (<main className="mx-auto max-w-6xl px-6 md:px-8 py-12">
            <section className="max-w-2xl">
                <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Blog</h1>
                <p className="mt-3 text-muted-foreground">
                    Product announcements, setup guides, and field notes from running offline campaigns.
                </p>
            </section>

            <Separator className="my-10"/>

            <div className="grid gap-4 sm:grid-cols-2">
                {POSTS.map((post) => (<PostCard key={post.slug} post={post}/>))}
            </div>
        </main>);
}
function PostCard({ post }) {
    return (<Card className="h-full transition hover:shadow-md hover:bg-muted/30">
            <CardHeader className="space-y-2">
                <div className="flex items-center justify-between">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
                        {post.icon}
                    </span>
                    <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{post.tag}</span>
                </div>
                <CardTitle className="text-base leading-snug">{post.title}</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-3">
                <p>{post.description}</p>
                <div className="flex items-center justify-between pt-2">
                    <Link to={`/blog/${post.slug}`} className="inline-flex items-center gap-1 text-primary font-medium hover:underline">
                        Read article
                        <ArrowRight className="h-3.5 w-3.5"/>
                    </Link>
                    <span className="text-[11px] text-muted-foreground/70">{post.date}</span>
                </div>
            </CardContent>
        </Card>);
}
